import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Button,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { SnackbarProvider } from "notistack";
import AttentionNeededEmployeesTable from "./AttentionNeededEmployeesTable";
import MatchedEmployeesTable from "./MatchedEmployeesTable";
import { getProfitCenters } from "../../../../api";
import CustomPagination from "../../../../Components/CustomPagination";
import PayrollsPopup from "../../../../Components/PayrollPopup";
import { useAlerts, useFetchPayrollDetails } from "../../../../Hooks";
import { Loader } from "../../../../Components/Loader";

import shortcutManager from "../../../../Utilities/ShortcutManager";
import AddEmplyeePopover from "../../../../Components/AddEmplyeePopover";
import { useFormikContext } from "formik";
import SharedAttachments from "../../../../Components/SharedAttachments";

const toggleGroupSx = {
  borderRadius: 1,
  border: "2px solid #b5dcf2",
  backgroundColor: "#eaf5fc",
  width: "40%",
  minWidth: 260,
  height: 50,
  "& .MuiToggleButton-root": {
    flex: 1,
    textTransform: "none",
    fontWeight: 600,
    fontSize: 12,
    color: "#3D5C7B",
    border: "none",
    borderRadius: 0,
    px: 2,
    "&:hover": { backgroundColor: "#eaf5fc" },
  },
  "& .MuiToggleButtonGroup-grouped:not(:first-of-type)": {
    borderLeft: "1px solid #b5dcf2",
  },
  "& .MuiToggleButton-root.Mui-selected": {
    backgroundColor: "white",
    color: "#3D5C7B",
    borderBottom: "3px solid #2782EC",
    "&:hover": { backgroundColor: "white" },
  },
};

const actionButtonSx = {
  px: 3,
  py: 0.75,
  fontSize: 12,
  backgroundColor: "#1e98d7",
  color: "white",
  "&:hover": { backgroundColor: "#a7d2ea" },
};


const PayPeriodEmployeeReview = ({ onClose, setStep, setSelectedRow, accountOptions, payrollId, corporationId, selectedRow }) => {
  const [refreshKey, setRefreshKey] = useState(0);
  const [addEployeePopup, setAddEmployeePopup] = useState(null);
  const [showProfitCenter, setShowProfitCenter] = useState(false);

  const { values } = useFormikContext();

  const { popup: alertPopup, showCancelConfirm, hidePopup } = useAlerts();
  const { activePage, setActivePage, handleTabChange, matchedRows, attentionRows, payrollTotals, attentionCount, completedCount, page, setPage, rowsPerPage, totalRecords, dataLoading } = useFetchPayrollDetails(payrollId ? payrollId : values?.payRollID, refreshKey);

  useEffect(() => {
    if (selectedRow && setActivePage) {
      if (selectedRow.statusText === "Unmapped" || selectedRow.view === "Unmapped") {
        setActivePage("attention");
      } else if (selectedRow.statusText === "Matched" || selectedRow.view === "Matched" || selectedRow.statusText === "Completed" || selectedRow.view === "Completed") {
        setActivePage("matched");
      } else {
        setActivePage("attention");
      }
      setSelectedRow(null);
    }
  }, [selectedRow, setActivePage, setSelectedRow]);

  useEffect(() => {
    const fetchProfitCentersData = async () => {
      const corpId = corporationId || values?.corpId || values?.corporationId;
      if (!corpId) {
        setShowProfitCenter(false);
        return;
      }
      try {
        const pcList = await getProfitCenters(corpId);
        setShowProfitCenter(pcList && pcList.length > 0);
      } catch (error) {
        console.error("Error fetching profit centers:", error);
        setShowProfitCenter(false);
      }
    };

    fetchProfitCentersData();
  }, [corporationId, values?.corpId, values?.corporationId]);

  const handleToggle = (event, newPage) => {
    const targetTab = newPage !== null ? newPage : activePage;
    handleTabChange(targetTab);
  };


  const handleUpdateAndClose = useCallback(() => {
    const hasAttention = Boolean(attentionCount && attentionCount > 0);
    if (!hasAttention) {
      onClose();
    } else {
      showCancelConfirm("Records pending. Do you want to save progress and return?", () => {
        onClose();
      });
    }
  }, [attentionCount, onClose, showCancelConfirm]);

  useEffect(() => {
    if (alertPopup.open) {
      return;
    }

    shortcutManager.addShortcut("alt+c", () => {
      onClose();
    });
    shortcutManager.addShortcut("alt+b", () => {
      setStep(1);
    });
    shortcutManager.addShortcut("alt+s", () => {
      if (activePage !== "attention") onClose();
    });
    shortcutManager.addShortcut("alt+p", () => {
      if (activePage === "attention") {
        handleUpdateAndClose();
      } else {
        onClose();
      }
    });

    return () => {
      shortcutManager.removeShortcut("alt+c");
      shortcutManager.removeShortcut("alt+b");
      shortcutManager.removeShortcut("alt+s");
      shortcutManager.removeShortcut("alt+p");
    };
  }, [activePage, alertPopup.open, handleUpdateAndClose, onClose, setStep]);

  const currentRows = activePage === "attention" ? attentionRows : matchedRows;
  const currentTabCount = activePage === "attention" ? attentionCount : completedCount;
  const countToUse = (currentTabCount !== undefined && currentTabCount !== null)
    ? currentTabCount
    : (totalRecords || 0);
  const totalPages = Math.ceil(countToUse / (rowsPerPage || 40)) || 1;

  const rowsToDisplay = currentRows.length > rowsPerPage
    ? currentRows.slice(page * rowsPerPage, (page + 1) * rowsPerPage)
    : currentRows;

  const paginatedRows = rowsToDisplay.map((r) => ({
    ...r,
    profitCenter: r.profitCenter || r.raw?.PCName || r.raw?.ProfitCenter || "",
    pageNum: page + 1,
  }));

  const allEmployeeIds = [
    ...attentionRows.map(r => ({
      EmployeeId: r?.raw?.EmployeeID,
      IncDeptID: r?.raw?.DepartmentID,
      JobID: r?.raw?.JobID,
      PCID: r?.raw?.PCID || r?.raw?.ProfitCenterID || r?.profitCenterId || r?.PCID || "",
    })),
    ...matchedRows.map(r => ({
      EmployeeId: r?.raw?.EmployeeID,
      IncDeptID: r?.raw?.DepartmentID,
      JobID: r?.raw?.JobID,
      PCID: r?.raw?.PCID || r?.raw?.ProfitCenterID || r?.profitCenterId || r?.PCID || "",
    })),
  ];

  useEffect(() => {
    // When this view is active, ensure the main application menu is hidden.
    window.parent.postMessage("HideMenu", "*");

    // The parent component (PayPeriodSetup) is responsible for showing the menu on final close.
    // So, no cleanup is needed here.
  }, []);

  return (
    <SnackbarProvider
      maxSnack={3}
      anchorOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      autoHideDuration={3000}
    >
      <Box width="100%" display="flex" flexDirection="column" sx={{ height: "100%" }}>
        {dataLoading && <Loader loading={dataLoading} />}
        <Stack direction="row" alignItems="center" gap={2}>
          <ToggleButtonGroup
            value={activePage}
            exclusive
            onChange={handleToggle}
            sx={toggleGroupSx}
          >
            <ToggleButton
              value="attention"
              aria-label="attention"
            >
              Attention Needed ({attentionCount ?? 0})
            </ToggleButton>
            <ToggleButton
              value="matched"
              aria-label="matched"
            >
              Completed / Matched ({completedCount ?? 0})
            </ToggleButton>
          </ToggleButtonGroup>

          <Box sx={{ flexGrow: 1 }} />

          <Stack direction="row" spacing={1}>
            {activePage === "attention" ? (
              <>
                <Button onClick={() => setStep(1)} variant="contained" size="small" sx={actionButtonSx}>
                  <u>B</u>ack
                </Button>
                <Button onClick={handleUpdateAndClose} variant="contained" size="small" sx={actionButtonSx}>
                  <span><u>C</u>lose</span>
                </Button>
                {/* <Button onClick={onClose} variant="contained" size="small" sx={neutralButtonSx}>
                <u>C</u>ancel
              </Button> */}
              </>
            ) : (
              <>
                <Button onClick={() => setStep(1)} variant="contained" size="small" sx={actionButtonSx}>
                  <u>B</u>ack
                </Button>
                {/* <Button onClick={onClose} variant="contained" size="small" sx={actionButtonSx}>
                <u>S</u>ave & Close
              </Button> */}
                <Button onClick={onClose} variant="contained" size="small" sx={actionButtonSx}>
                  <span><u>C</u>lose</span>
                </Button>
              </>
            )}
          </Stack>
        </Stack>

        <Box sx={{
          mt: 2,
          // flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          "&::-webkit-scrollbar": {
            width: 5,
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#bdbdbd",
            borderRadius: 10,
          },
          height: "100%"
        }}>
          <Box sx={{
            height: "calc(100% - 80px)",
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
          }}>

            {activePage === "attention" ? (
              <AttentionNeededEmployeesTable
                setStep={setStep}
                setSelectedRow={setSelectedRow}
                accountOptions={accountOptions}
                rows={paginatedRows}
                totals={payrollTotals?.attention}
                setAddEmployeePopup={setAddEmployeePopup}
                showProfitCenter={showProfitCenter}
              />
            ) : (
              <MatchedEmployeesTable
                setStep={setStep}
                setSelectedRow={setSelectedRow}
                accountOptions={accountOptions}
                rows={paginatedRows}
                totals={payrollTotals?.matched}
                setAddEmployeePopup={setAddEmployeePopup}
                showProfitCenter={showProfitCenter}
              />
            )}

          </Box>

          <Box sx={{ my: 0.5, flexShrink: 0 }}>
            <SharedAttachments
              documentType="PayrollEntry"
              refIdOverride={payrollId}
              corpIdOverride={corporationId || values?.corpId || values?.corporationId}
              directUpload={true}
              showPreview={false}
            />
          </Box>
        </Box>

        <Stack direction="row" justifyContent="flex-end" alignItems="center"
          sx={{
            height: "30px",
            background: "linear-gradient(to bottom, #e6e6e6 0%, #f2f2f2 50%, #e6e6e6 100%)",
            borderTop: "1px solid #cfcfcf",
            borderBottom: "1px solid #cfcfcf",
            mt: "auto",
          }}>
          <CustomPagination
            count={totalPages}
            page={page}
            onChange={(e, newPage) => setPage(newPage - 1)}
          />
        </Stack>


        <PayrollsPopup
          open={alertPopup.open}
          popupType={alertPopup.type}
          message={alertPopup.message}
          onConfirm={alertPopup.onConfirm}
          onCancel={hidePopup}
        />
        {
          addEployeePopup &&
          <AddEmplyeePopover
            open={addEployeePopup}
            onClose={() => setAddEmployeePopup(null)}
            corporationId={corporationId}
            showProfitCenter={showProfitCenter}
            prevSelectedEmpls={allEmployeeIds}
            onSuccess={() => {
              setRefreshKey(prev => prev + 1)
              setActivePage("attention")
            }}

          />
        }
      </Box>
    </SnackbarProvider>
  );
};

export default PayPeriodEmployeeReview;
